import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import InviteLinkJoin from '@/components/InviteLinkJoin';
import type { InviteLinkJoinErrorReason, InviteLinkJoinStatus } from '@/components/InviteLinkJoin';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { useAuth } from '@/hooks/useAuth';
import { useTelegram } from '@/hooks/useTelegram';
import { apiClient } from '@/services/api';
import { INVITE_LINK_ERRORS } from '@/constants';
import type { InviteLinkError } from '@/constants';
import type { AcceptInviteLinkResult, InviteLinkPreview } from '@/types/inviteLink';
import { withNextParam } from '@/utils/authRedirect';
import { pendingInvite } from '@/utils/pendingInvite';
import {
  buildJoinPath,
  buildTelegramStartAppUrl,
  getApiErrorMessage,
  getInviteLinkErrorCode,
  isInviteLinkTokenFormat,
} from '@/utils/inviteLink';

const HTTP_TOO_MANY_REQUESTS = 429;

const getHttpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * Public landing for invite links / QR codes (`/join/:token`). Shows a tournament preview and
 * requires an explicit "Join" click; anonymous visitors get sign-up / log-in CTAs that come back
 * here via `?next=` (and the pending-invite fallback for the Telegram welcome flow).
 */
const JoinByLinkPage = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading: isAuthLoading, isTelegramAvailable, loginWithTelegram } = useAuth();
  const { isLoaded: isTelegramLoaded, isInTelegram } = useTelegram();

  usePageConfig({ title: t('inviteLink.join.title'), showHeader: false, showBottomNav: false, showNavigation: false });

  const [acceptError, setAcceptError] = useState<InviteLinkError | null>(null);
  const [isTelegramLoginPending, setIsTelegramLoginPending] = useState(false);

  const hasValidTokenFormat = isInviteLinkTokenFormat(token);

  const previewQuery = useQuery<InviteLinkPreview>({
    queryKey: ['invite-link-preview', token],
    queryFn: async () => {
      const response = await apiClient.getInviteLinkPreview(token as string);
      return response.data.data;
    },
    enabled: hasValidTokenFormat,
    retry: false,
    staleTime: 0,
  });

  const acceptMutation = useMutation<AcceptInviteLinkResult, unknown>({
    mutationFn: async () => {
      const response = await apiClient.acceptInviteLink(token as string);
      return response.data.data;
    },
    onSuccess: ({ tournamentId, alreadyMember }) => {
      pendingInvite.clear();
      const id = String(tournamentId);
      queryClient.invalidateQueries({ queryKey: ['my-tournaments'] });
      queryClient.invalidateQueries({ queryKey: ['public-tournaments'] });
      queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      queryClient.invalidateQueries({ queryKey: ['tournament-participants', id] });
      queryClient.invalidateQueries({ queryKey: ['tournament-leaderboard', id] });
      queryClient.invalidateQueries({ queryKey: ['tournaments-by-league'] });
      queryClient.invalidateQueries({ queryKey: ['match-tournaments'] });
      queryClient.invalidateQueries({ queryKey: ['invite-link-preview', token] });
      if (alreadyMember) {
        toast.info(t('inviteLink.join.alreadyMember'), { toastId: 'invite-link-already-member' });
      } else {
        toast.success(t('inviteLink.join.joined'), { toastId: 'invite-link-joined' });
      }
      navigate(`/tournaments/${id}`, { replace: true });
    },
    onError: (error) => {
      const code = getInviteLinkErrorCode(error);
      if (code) {
        setAcceptError(code);
        return;
      }
      toast.error(getHttpStatus(error) === HTTP_TOO_MANY_REQUESTS
        ? t('inviteLink.errors.tooManyRequests')
        : getApiErrorMessage(error) ?? t('inviteLink.errors.generic'));
    },
  });

  const preview = previewQuery.data ?? null;
  const alreadyMemberTournamentId = preview?.alreadyMember ? preview.tournament?.id ?? null : null;
  const previewReason: InviteLinkError | null = preview && !preview.valid
    ? preview.reason ?? INVITE_LINK_ERRORS.NOT_FOUND
    : getInviteLinkErrorCode(previewQuery.error);
  // Link-level failure (the link itself is dead) vs transient failures (rate limit, network).
  const linkErrorReason: InviteLinkError | null = !hasValidTokenFormat
    ? INVITE_LINK_ERRORS.NOT_FOUND
    : acceptError ?? previewReason;
  let transientErrorReason: InviteLinkJoinErrorReason | null = null;
  if (previewQuery.isError) {
    transientErrorReason = getHttpStatus(previewQuery.error) === HTTP_TOO_MANY_REQUESTS ? 'tooManyRequests' : 'generic';
  }
  const errorReason: InviteLinkJoinErrorReason | null = linkErrorReason ?? transientErrorReason;

  let status: InviteLinkJoinStatus = 'ready';
  if (alreadyMemberTournamentId !== null) status = 'loading';
  else if (errorReason) status = 'error';
  else if (isAuthLoading || previewQuery.isLoading) status = 'loading';

  // Members go straight to the tournament, whatever the link state (the backend redeem agrees).
  useEffect(() => {
    if (alreadyMemberTournamentId === null) return;
    pendingInvite.clear();
    toast.info(t('inviteLink.join.alreadyMember'), { toastId: 'invite-link-already-member' });
    navigate(`/tournaments/${alreadyMemberTournamentId}`, { replace: true });
  }, [alreadyMemberTournamentId, navigate, t]);

  // Keep the token across the auth flow for anonymous visitors; drop it once the link is dead.
  // Transient errors (429, network) keep it so the visitor can retry after authenticating.
  useEffect(() => {
    if (isAuthLoading || !token) return;
    if (linkErrorReason) {
      pendingInvite.clear();
    } else if (!isAuthenticated && hasValidTokenFormat) {
      pendingInvite.set(token);
    }
  }, [isAuthLoading, isAuthenticated, token, hasValidTokenFormat, linkErrorReason]);

  const handleTelegramLogin = async () => {
    setIsTelegramLoginPending(true);
    try {
      // Existing users are signed in in place; new users are routed to /telegram-welcome,
      // which resumes this invite via the pending-invite token.
      await loginWithTelegram();
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t('inviteLink.errors.generic'));
    } finally {
      setIsTelegramLoginPending(false);
    }
  };

  const joinPath = hasValidTokenFormat ? buildJoinPath(token) : null;
  const viewerName = user ? (user.first_name || user.username || user.email || '') : null;

  return (
    <InviteLinkJoin
      status={status}
      preview={preview}
      errorReason={errorReason}
      viewerName={viewerName}
      isJoining={acceptMutation.isPending}
      onJoin={() => acceptMutation.mutate()}
      anonymousActions={{
        signUpHref: withNextParam('/sign-up', joinPath),
        logInHref: withNextParam('/login', joinPath),
        telegramAppUrl: hasValidTokenFormat && isTelegramLoaded && !isInTelegram
          ? buildTelegramStartAppUrl(token)
          : null,
        onTelegramLogin: isInTelegram && isTelegramAvailable ? handleTelegramLogin : null,
        isTelegramLoginPending,
      }}
      onGoHome={() => {
        pendingInvite.clear();
        navigate(isAuthenticated ? '/dashboard' : '/', { replace: true });
      }}
    />
  );
};

export default JoinByLinkPage;
